// src/pages/StatsPage.jsx -- the Stats surface orchestrator (v1b).
//
// Single job: own search/player-selection state and the §I page-state machine,
// fetch from /api/player-stats, hand raw rows to src/lib/player-stats-display.js,
// and render subcomponents with the computed results. It contains NO stat math --
// median, hit-rate, role detection, and the query normalizer all live in the
// display module (spec v1b-amendment §C1/§C2). App.js passes the four navigation
// props (§A5) straight through; v1b exercises initialSearchQuery only.

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { captureMessage } from '../lib/errorReporting';
import { apiFetch } from '../lib/api';
import {
  normalizeStatsQuery, MIN_QUERY_LEN, MAX_QUERY_LEN, STAT_DEFINITIONS,
  resolveRolesForPlayer, rolesForZeroGame, getChipsForRoles,
  computeMedianLine, computeHitRate, computeRecentFormStrip, isValidSample,
  resolvePrimaryRole, getStatDisplayValue, buildStripCaption, derivePlayerDisplay,
  getSeasonStatRows, deriveSeasonLabel, rosterStatusBadge,
} from '../lib/player-stats-display';
import SearchResults from '../components/stats/SearchResults';
import PlayerHeader from '../components/stats/PlayerHeader';
import SeasonStatsBar from '../components/stats/SeasonStatsBar';
import RecentFormStrip from '../components/stats/RecentFormStrip';
import StatFocusChips from '../components/stats/StatFocusChips';
import HitRateCard from '../components/stats/HitRateCard';
import PlayerGameLog from '../components/stats/PlayerGameLog';

const SEARCH_DEBOUNCE_MS = 300;
// #852: mirrors GAME_LOG_LIMIT in api/lib/player-stats-query.js. A full page implies older games remain.
const GAME_LOG_PAGE_SIZE = 25;

// --- §I state machine (pure, exported for direct unit testing) --------------
// Precedence (high -> low): rate_limited > recoverable_error > player_loading >
// search_loading > insufficient_sample > player_empty > search_empty >
// query_too_short > deep_link_unresolved > idle > ready. player_empty (0 rows)
// and insufficient_sample (1-2 valid) are mutually exclusive by definition, so
// the 0-row check resolving to player_empty does not violate the ordering.
export function deriveStatsPageState({
  normalizedQuery, searchStatus, playerStatus, selectedPlayer,
  gameLogCount, validSampleCount, deepLinkActive,
}) {
  if (playerStatus === 'rate_limited' || searchStatus === 'rate_limited') return 'rate_limited';
  if (playerStatus === 'error' || searchStatus === 'error') return 'recoverable_error';
  if (playerStatus === 'loading') return 'player_loading';
  if (searchStatus === 'loading') return 'search_loading';

  if (selectedPlayer) {
    if (gameLogCount === 0) return 'player_empty';
    if (validSampleCount < 3) return 'insufficient_sample';
    return 'ready';
  }

  // P1#2: while a deep-link is still unresolved, an empty auto-search result is
  // the deep-link fallback -- not a generic "no players found" -- even though
  // search_empty normally outranks deep_link_unresolved.
  if (searchStatus === 'empty') return deepLinkActive ? 'deep_link_unresolved' : 'search_empty';
  if (normalizedQuery.length > 0 && normalizedQuery.length < MIN_QUERY_LEN) return 'query_too_short';
  if (deepLinkActive) return 'deep_link_unresolved';
  return 'idle';
}

// Returns the parsed body, or null when a 200 is not JSON (wrong content-type or a body
// that does not parse). Null is a SERVICE failure -- an HTML error page from the edge, a
// misrouted request -- and every caller maps it to the recoverable_error state with Retry.
// It used to return {} here, which the state machine read as "no players / no games" and
// rendered as "this player may not have played recently" (UI critique item B). Recoverable
// via Retry -> captureMessage at warning, not captureException (CC-IMPL-037). One log per miss.
async function safeJson(res) {
  const ct = res.headers?.get?.('content-type') || '';
  if (!ct.includes('application/json')) {
    captureMessage('[stats] non-JSON 200 (degraded)', {
      level: 'warning', tags: { function: 'StatsPage', stage: 'safe-json' }, extra: { contentType: ct },
    });
    return null;
  }
  try {
    return await res.json();
  } catch (err) {
    captureMessage('[stats] response parse failed (degraded)', {
      level: 'warning', tags: { function: 'StatsPage', stage: 'safe-json' }, extra: { message: err.message },
    });
    return null;
  }
}

const Message = ({ children }) => (
  <div style={{ color: '#94a3b8', fontSize: 14, textAlign: 'center', padding: '28px 12px' }}>{children}</div>
);

function ErrorRetry({ text, onRetry }) {
  return (
    <div style={{ textAlign: 'center', padding: '28px 12px' }}>
      <div style={{ color: '#cbd5e1', fontSize: 14, marginBottom: 12 }}>{text}</div>
      <button type="button" onClick={onRetry}
        style={{ cursor: 'pointer', fontFamily: 'inherit', fontWeight: 800, fontSize: 13, color: '#fff',
          background: 'linear-gradient(135deg,#1d4ed8,#4f46e5)', border: 'none', borderRadius: 999, padding: '10px 24px' }}>
        Retry
      </button>
    </div>
  );
}

export default function StatsPage({
  initialSearchQuery = '', initialPlayerId = null, initialPlayerLeague = null,
  initialFocusStat = null, highlightGameId = null,
}) {
  const [query, setQuery] = useState(initialSearchQuery);
  const [searchStatus, setSearchStatus] = useState('idle'); // idle|loading|results|empty|error|rate_limited
  const [searchResults, setSearchResults] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [gameLog, setGameLog] = useState([]);
  const [seasonStats, setSeasonStats] = useState(null);
  const [rosterMetadata, setRosterMetadata] = useState(null); // Issue 2: detail-view roster contract
  // #852 game-log pagination: hasMore (a full page implies older games remain) + a
  // load-more in-flight flag driving the button.
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [playerStatus, setPlayerStatus] = useState('idle'); // idle|loading|loaded|error|rate_limited
  const [focusStatId, setFocusStatId] = useState(initialFocusStat);
  const [mode, setMode] = useState('over');
  const [lineOverride, setLineOverride] = useState(null);
  const [deepLinkActive, setDeepLinkActive] = useState(Boolean(initialSearchQuery));
  const lastAction = useRef(null); // { type: 'search'|'player', arg }
  // Monotonic request token: only the most recently dispatched fetch may commit
  // its result. Guards against out-of-order resolutions (fast-then-slow taps)
  // rendering a stale player's stats under the current identity (pre-Codex).
  const reqSeq = useRef(0);

  const normalizedQuery = normalizeStatsQuery(query);
  const queryTooLong = normalizedQuery.length > MAX_QUERY_LEN; // P1#4: same layer as query_too_short

  // --- fetch helpers --------------------------------------------------------
  const handleHttpError = useCallback((status, phase) => {
    const setStatus = phase === 'search' ? setSearchStatus : setPlayerStatus;
    if (status === 429) { setStatus('rate_limited'); return; }
    // 5xx / other non-2xx -> recoverable client-side; log at warning (CC-IMPL-037).
    captureMessage(`[stats] ${phase} request failed`, {
      level: 'warning', tags: { function: 'StatsPage', stage: phase }, extra: { status },
    });
    setStatus('error');
  }, []);

  const runSearch = useCallback(async (nq) => {
    const my = ++reqSeq.current;
    lastAction.current = { type: 'search', arg: nq };
    setSearchStatus('loading');
    try {
      const res = await apiFetch(`/api/player-stats?q=${encodeURIComponent(nq)}`);
      if (my !== reqSeq.current) return; // a newer request superseded this one
      if (!res.ok) return handleHttpError(res.status, 'search');
      const data = await safeJson(res);
      if (my !== reqSeq.current) return;
      if (data === null) { setSearchStatus('error'); return; } // non-JSON 200: service failure, Retry
      const players = Array.isArray(data.players) ? data.players : [];
      setSearchResults(players);
      setSearchStatus(players.length ? 'results' : 'empty');
    } catch (err) {
      if (my !== reqSeq.current) return;
      // Network throw recovers via the Retry button -> captureMessage at warning
      // (CC-IMPL-037; pre-Codex rule-compliance fix).
      captureMessage('[stats] search request failed (recoverable)', {
        level: 'warning', tags: { function: 'StatsPage', stage: 'search' }, extra: { message: err.message },
      });
      setSearchStatus('error');
    }
  }, [handleHttpError]);

  const loadGameLog = useCallback(async (playerId, league) => {
    // v1b Deliverable B: the game-log route requires &league=. The v1c deep-link-by-id path
    // (initialPlayerId) has no league yet -- skip the load rather than fire a guaranteed-400.
    // The player stub still renders (player_empty), no crash, no infinite-retry error dead-end.
    // Carry-forward: source a league for the deep-link path when v1c navigation activates.
    if (!league) return;
    const my = ++reqSeq.current;
    lastAction.current = { type: 'player', arg: playerId, league };
    setPlayerStatus('loading');
    try {
      const res = await apiFetch(`/api/player-stats?playerId=${encodeURIComponent(playerId)}&league=${encodeURIComponent(league)}`);
      if (my !== reqSeq.current) return; // a newer request superseded this one
      if (!res.ok) return handleHttpError(res.status, 'player');
      const data = await safeJson(res);
      if (my !== reqSeq.current) return;
      if (data === null) { setPlayerStatus('error'); return; } // non-JSON 200: service failure, Retry
      const games = Array.isArray(data.games) ? data.games : [];
      setGameLog(games);
      setSeasonStats(data.seasonStats ?? null);
      setRosterMetadata(data.rosterMetadata ?? null);
      // #852: a full first page implies older games remain; reset any prior load-more spinner.
      setHasMore(games.length === GAME_LOG_PAGE_SIZE);
      setLoadingMore(false);
      setPlayerStatus('loaded');
    } catch (err) {
      if (my !== reqSeq.current) return;
      // Network throw recovers via the Retry button -> captureMessage at warning
      // (CC-IMPL-037; pre-Codex rule-compliance fix).
      captureMessage('[stats] game-log request failed (recoverable)', {
        level: 'warning', tags: { function: 'StatsPage', stage: 'player' }, extra: { message: err.message },
      });
      setPlayerStatus('error');
    }
  }, [handleHttpError]);

  // #852 "Load more": append the next older page via the keyset cursor (the last loaded row's
  // game_date + game_id). Append-safe -- a failure keeps the loaded games visible and leaves the
  // button available for a retry; it NEVER flips playerStatus to the error view. reqSeq-guarded so
  // a player switch mid-flight can't append one player's games onto another.
  const loadMoreGames = useCallback(async () => {
    const last = gameLog[gameLog.length - 1];
    const action = lastAction.current;
    // Defense in depth: the RPC excludes null-dated rows, so the last row always has a usable
    // (game_date, game_id) cursor. If one ever arrives without it, stop paginating (hide the button)
    // rather than leave an inert "Load more".
    if (!last || !last.game_date || !last.game_id) { setHasMore(false); return; }
    if (!action || action.type !== 'player' || !action.league) return;
    const my = ++reqSeq.current;
    setLoadingMore(true);
    try {
      const url = `/api/player-stats?playerId=${encodeURIComponent(action.arg)}&league=${encodeURIComponent(action.league)}`
        + `&beforeDate=${encodeURIComponent(last.game_date)}&beforeGameId=${encodeURIComponent(last.game_id)}`;
      const res = await apiFetch(url);
      if (my !== reqSeq.current) return; // a newer request (player switch / retry) superseded this
      if (!res.ok) {
        // Keep the already-loaded page; log at warning; the button stays for a retry (CC-IMPL-037).
        captureMessage('[stats] game-log load-more failed', {
          level: 'warning', tags: { function: 'StatsPage', stage: 'player-more' }, extra: { status: res.status },
        });
        return;
      }
      const data = await safeJson(res);
      if (my !== reqSeq.current) return;
      if (data === null) return; // non-JSON 200: keep the loaded page; safeJson logged; button stays (finally clears the spinner)
      const next = Array.isArray(data.games) ? data.games : [];
      setGameLog((prev) => [...prev, ...next]);
      setHasMore(next.length === GAME_LOG_PAGE_SIZE);
    } catch (err) {
      if (my !== reqSeq.current) return;
      captureMessage('[stats] game-log load-more failed (recoverable)', {
        level: 'warning', tags: { function: 'StatsPage', stage: 'player-more' }, extra: { message: err.message },
      });
    } finally {
      if (my === reqSeq.current) setLoadingMore(false);
    }
  }, [gameLog]);

  // --- debounced search on the normalized query -----------------------------
  useEffect(() => {
    if (selectedPlayer) return undefined;
    // Fire only for a query the server would accept: too short (<MIN) OR too long
    // (>MAX) is a client-side validation, no request (P1#4). MAX mirrors the server.
    if (normalizedQuery.length < MIN_QUERY_LEN || normalizedQuery.length > MAX_QUERY_LEN) {
      // Invalidate any in-flight search so its late response can't repopulate stale
      // results under the now-invalid query (Codex P1). No replacement request will
      // bump reqSeq here, so we must bump it ourselves.
      reqSeq.current += 1;
      setSearchStatus('idle');
      setSearchResults([]);
      return undefined;
    }
    const t = setTimeout(() => runSearch(normalizedQuery), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [normalizedQuery, selectedPlayer, runSearch]);

  // --- v1c deep-link: open a player directly by id (interface locked in v1b) -
  useEffect(() => {
    if (initialPlayerId) {
      setDeepLinkActive(false);
      setSelectedPlayer({ player_id: initialPlayerId, name: '', team: '', league: initialPlayerLeague || '' });
      // v1c deep-link carries the leg's league (Deliverable B) -> league-qualified game log. If it
      // is somehow absent, loadGameLog's guard skips the fetch and the player stub renders empty.
      loadGameLog(initialPlayerId, initialPlayerLeague);
    }
    // Run once on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- derived model (all math in the display module) -----------------------
  const roles = useMemo(() => resolveRolesForPlayer(gameLog), [gameLog]);
  const chips = useMemo(() => getChipsForRoles(roles), [roles]);

  // Keep the focused stat valid for the current chip set.
  useEffect(() => {
    if (chips.length === 0) return;
    const stillValid = focusStatId && chips.some((c) => c.id === focusStatId);
    if (!stillValid) {
      setFocusStatId(chips[0].id);
      setLineOverride(null);
      setMode('over');
    }
  }, [chips, focusStatId]);

  const focusDef = useMemo(
    () => STAT_DEFINITIONS.find((d) => d.id === focusStatId) || chips[0] || null,
    [focusStatId, chips]
  );

  const validSampleCount = useMemo(
    () => (focusDef ? gameLog.filter((r) => isValidSample(r, focusDef)).length : 0),
    [gameLog, focusDef]
  );

  const primaryRole = useMemo(() => resolvePrimaryRole(gameLog), [gameLog]);
  const strip = useMemo(() => computeRecentFormStrip(gameLog, primaryRole), [gameLog, primaryRole]);

  const defaultLine = useMemo(() => (focusDef ? computeMedianLine(gameLog, focusDef) : 0), [gameLog, focusDef]);
  const line = lineOverride ?? defaultLine;
  const hitRate = useMemo(
    () => (focusDef ? computeHitRate(gameLog, focusDef, { mode, line }) : { cleared: 0, total: 0, pct: 0 }),
    [gameLog, focusDef, mode, line]
  );

  const stripCaption = useMemo(() => buildStripCaption(gameLog), [gameLog]);
  // #998 PR 3: a selection that predates the jersey column (or a cached row) carries
  // no jersey_number; the detail read's rosterMetadata does (same roster row as
  // roster_status). Fill it in only when the derived identity lacks one -- the
  // selection/game-row value always wins. Display-only.
  const headerPlayer = useMemo(() => {
    const p = derivePlayerDisplay(selectedPlayer, gameLog);
    if (p && p.jersey_number == null && rosterMetadata?.jersey_number != null) {
      return { ...p, jersey_number: rosterMetadata.jersey_number };
    }
    return p;
  }, [selectedPlayer, gameLog, rosterMetadata]);

  // Display roles: game-backed players use their game-row roles; a zero-game player
  // (IL/optioned, no rows) derives role from the roster position (Issue 2 §6) so the
  // season bar still renders. resolveRolesForPlayer returns [] for an empty log.
  const displayRoles = useMemo(
    () => (gameLog.length > 0 ? roles : rolesForZeroGame(rosterMetadata?.position, headerPlayer?.league)),
    [gameLog.length, roles, rosterMetadata, headerPlayer]
  );
  const seasonStatRows = useMemo(() => getSeasonStatRows(seasonStats, displayRoles), [seasonStats, displayRoles]);
  const seasonLabel = useMemo(() => deriveSeasonLabel(seasonStats), [seasonStats]);

  const pageState = deriveStatsPageState({
    normalizedQuery, searchStatus, playerStatus, selectedPlayer,
    gameLogCount: gameLog.length, validSampleCount, deepLinkActive,
  });

  // --- handlers -------------------------------------------------------------
  const onQueryChange = (e) => { setDeepLinkActive(false); setQuery(e.target.value); };
  const pickPlayer = (p) => {
    setDeepLinkActive(false);
    setSelectedPlayer(p);
    setSearchResults([]);
    // Reset search status on commit: a debounced search may have flipped it to
    // 'loading', and reqSeq makes the superseded search return without clearing it
    // -- leaving deriveStatsPageState stuck at search_loading over the player page
    // (blank render, no player-branch UI for that state). Codex P1.
    setSearchStatus('idle');
    setFocusStatId(initialFocusStat);
    setLineOverride(null);
    setMode('over');
    // The search result carries league (search RPC returns it) -> thread it into the game log.
    loadGameLog(p.player_id, p.league);
  };
  const backToSearch = () => {
    // Invalidate any in-flight game-log request so its late response can't commit
    // a sticky playerStatus (e.g. an error that deriveStatsPageState then ranks
    // above the search view) after we've navigated away (pre-Codex fail-closed fix).
    reqSeq.current += 1;
    setSelectedPlayer(null);
    setGameLog([]);
    setSeasonStats(null);
    setRosterMetadata(null);
    setPlayerStatus('idle');
    // #852: clear pagination state too, so returning to a different player never renders a
    // stale "Load more"/spinner for one frame before its first page loads.
    setHasMore(false);
    setLoadingMore(false);
  };
  const retry = () => {
    const a = lastAction.current;
    if (!a) return;
    if (a.type === 'search') runSearch(a.arg);
    else loadGameLog(a.arg, a.league);
  };
  const changeLine = (delta) => setLineOverride((prev) => Math.max(0, (prev ?? defaultLine) + delta));

  // Delegates to the display module -- StatsPage does no field access or coercion.
  const logGetValue = useCallback((row) => (focusDef ? getStatDisplayValue(row, focusDef) : null), [focusDef]);

  // --- render ---------------------------------------------------------------
  const container = { padding: '16px 16px 40px', maxWidth: 640, margin: '0 auto' };

  if (selectedPlayer) {
    return (
      <div style={container}>
        <button type="button" onClick={backToSearch}
          style={{ cursor: 'pointer', fontFamily: 'inherit', background: 'transparent', border: 'none', color: '#94a3b8', fontWeight: 700, fontSize: 13, padding: '0 0 10px' }}>
          ← Search
        </button>

        {pageState === 'player_loading' && <Message>Loading player…</Message>}
        {pageState === 'rate_limited' && <ErrorRetry text="Too many requests. Try again in a moment." onRetry={retry} />}
        {pageState === 'recoverable_error' && <ErrorRetry text="Couldn’t load this player. Check your connection." onRetry={retry} />}

        {pageState === 'player_empty' && (
          <>
            <PlayerHeader player={headerPlayer} />
            {(() => {
              // Issue 2: IL/optioned label from rosterMetadata.roster_status (never
              // inferred from empty games); generic fallback when there is no status.
              const badge = rosterStatusBadge(rosterMetadata?.roster_status);
              const detail = badge
                ? `${badge.label === 'IL' ? 'On the injured list' : 'Optioned to the minors'} (${badge.title}).`
                : 'This player may be on the injured list or has not played recently.';
              return <Message>No recent games in collection window. {detail}</Message>;
            })()}
            {/* Season totals still render for a zero-game player (Issue 2 §6/DoD): role
                comes from the roster position via displayRoles. */}
            {seasonStatRows.length > 0 && (
              <SeasonStatsBar rows={seasonStatRows} seasonLabel={seasonLabel} />
            )}
          </>
        )}

        {(pageState === 'insufficient_sample' || pageState === 'ready') && (
          <>
            <PlayerHeader player={headerPlayer} />
            <RecentFormStrip strip={strip} caption={stripCaption} />
            <StatFocusChips chips={chips} focusStatId={focusDef?.id} onSelect={(id) => { setFocusStatId(id); setLineOverride(null); setMode('over'); }} />
            {pageState === 'ready' && focusDef && (
              <HitRateCard statDef={focusDef} mode={mode} line={line} hitRate={hitRate}
                onToggleMode={setMode} onLineChange={changeLine} />
            )}
            {pageState === 'insufficient_sample' && (
              <Message>Not enough games collected yet to show a hit rate (need 3+). The game log below is everything collected so far.</Message>
            )}
            {/* Always render the log in insufficient/ready: no-participation players
                have no chips (focusDef null) but their rows must stay visible (§A4). */}
            <PlayerGameLog rows={gameLog} statDef={focusDef} getValue={logGetValue} highlightGameId={highlightGameId} />
            {hasMore && (
              <div style={{ textAlign: 'center', padding: '12px 0 4px' }}>
                <button type="button" onClick={loadMoreGames} disabled={loadingMore}
                  style={{ cursor: loadingMore ? 'default' : 'pointer', fontFamily: 'inherit', fontWeight: 800,
                    fontSize: 13, color: '#fff', background: 'linear-gradient(135deg,#1d4ed8,#4f46e5)',
                    border: 'none', borderRadius: 999, padding: '10px 24px', opacity: loadingMore ? 0.6 : 1 }}>
                  {loadingMore ? 'Loading…' : 'Load more'}
                </button>
              </div>
            )}
            <SeasonStatsBar rows={seasonStatRows} seasonLabel={seasonLabel} />
          </>
        )}
      </div>
    );
  }

  return (
    <div style={container}>
      <input
        type="text"
        value={query}
        onChange={onQueryChange}
        placeholder="Search players"
        aria-label="Search players"
        autoCapitalize="none"
        autoCorrect="off"
        style={{
          width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', fontSize: 15,
          background: '#0b1626', border: '1px solid #1e293b', borderRadius: 12,
          padding: '13px 14px', color: '#fff', outline: 'none',
        }}
      />

      {queryTooLong && <Message>Keep your search under {MAX_QUERY_LEN} characters.</Message>}
      {!queryTooLong && pageState === 'query_too_short' && <Message>Type at least 2 characters to search.</Message>}
      {pageState === 'search_loading' && <Message>Searching…</Message>}
      {pageState === 'search_empty' && <Message>No recent stats found. This player may not have played recently — try their full name or last name only.</Message>}
      {pageState === 'rate_limited' && <ErrorRetry text="Too many requests. Try again in a moment." onRetry={retry} />}
      {pageState === 'recoverable_error' && <ErrorRetry text="Couldn’t reach the stats service. Check your connection." onRetry={retry} />}
      {pageState === 'deep_link_unresolved' && searchResults.length === 0 && (
        <Message>We couldn’t match that player automatically — search above to find them.</Message>
      )}
      {pageState === 'idle' && searchResults.length === 0 && normalizedQuery.length === 0 && (
        <Message>Search any NHL, MLB or NFL player to see recent form and hit rates.</Message>
      )}

      {searchResults.length > 0 && <SearchResults players={searchResults} onPick={pickPlayer} />}
    </div>
  );
}
